import { useParams, Link } from 'react-router-dom';
import { articles, movies } from '@/mocks/data';
import SEOMeta from '@/components/base/SEOMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import RelatedArticles from './components/RelatedArticles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = articles.find((a) => a.id === articleId);
  const movie = movies.find((m) => m.id === articleId);

  if (!article && !movie) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full max-w-[1280px] mx-auto px-4 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/40">
              <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-50 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-6">该页面不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-950 font-medium text-sm cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = article
    ? `${article.title} - 嫩草影院`
    : movie
      ? `${movie.title} - 嫩草影院高清在线观看`
      : '嫩草影院';

  const pageDescription = article
    ? article.excerpt
    : movie
      ? `${movie.title} - ${movie.categoryName}，${movie.year}年上映，导演${movie.director}，主演${movie.cast.join('、')}。${movie.description}`
      : '';

  const breadcrumbItems = article
    ? [
        { label: '首页', to: '/' },
        { label: '影视资讯', to: '/category/dianying' },
        { label: article.title },
      ]
    : [
        { label: '首页', to: '/' },
        { label: movie!.categoryName, to: `/category/${movie!.category}` },
        { label: movie!.title },
      ];

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={pageDescription}
        keywords={`嫩草影院,${article ? article.title : movie?.title},高清影视,在线观看,嫩草影院电影`}
        canonicalUrl={`https://www.nencaoyingyuan.com/article/${articleId}`}
        ogType="article"
        articlePublishedTime={article?.date || ''}
      />

      <SchemaOrg type="Organization" />
      {article && (
        <SchemaOrg
          type="Article"
          data={{
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            image: article.image,
            url: `https://www.nencaoyingyuan.com/article/${article.id}`,
          }}
        />
      )}

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {article ? (
              <article>
                <header className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 text-xs rounded bg-secondary-100/20 text-secondary-400 whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-600">{article.date}</span>
                  </div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading leading-tight mb-4">
                    {article.title}
                  </h1>
                </header>

                <div className="mb-8 rounded-lg overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full aspect-video object-cover"
                  />
                </div>

                <div
                  className="prose prose-invert max-w-none text-foreground-200 leading-relaxed text-sm md:text-base"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200/20">
                  <RelatedArticles currentId={article.id} />
                </div>
              </article>
            ) : movie ? (
              <article>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-6">
                  {movie.title}
                </h1>

                <div className="flex flex-col md:flex-row gap-6 mb-8">
                  <div className="w-full md:w-[240px] flex-shrink-0">
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={movie.image}
                        alt={`${movie.title} 电影海报`}
                        title={`${movie.title} - 嫩草影院高清在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 rounded-full text-sm font-bold bg-primary-500/20 text-primary-400">
                        {movie.rating} 分
                      </span>
                      <span className="text-sm text-foreground-400">{movie.year}</span>
                      <span className="text-sm text-foreground-400">{movie.categoryName}</span>
                      <span className="text-sm text-foreground-400">{movie.duration}</span>
                    </div>

                    <p className="text-sm text-foreground-300 leading-relaxed mb-4">
                      {movie.description}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-foreground-500">导演：</span>
                        <span className="text-foreground-200">{movie.director}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">主演：</span>
                        <span className="text-foreground-200">{movie.cast.join('、')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-10">
                  <h2 className="text-lg font-bold text-foreground-50 font-heading mb-4">
                    影片详情
                  </h2>
                  <p className="text-sm text-foreground-300 leading-relaxed">
                    {movie.description}
                  </p>
                </div>

                <div className="mt-10 pt-8 border-t border-background-200/20">
                  <RelatedArticles currentId={movie.id} />
                </div>
              </article>
            ) : null}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}